/**
 * Compact Discovery Panel
 * Left sidebar: shows discovered fish species with colored lock indicators.
 * Tap a fish row to expand quality lock grid inline.
 */

import { useState } from 'react';
import { useGameStore } from '@/state';
import { getFishDefinition, getZoneDefinition } from '@/data';
import { Quality, QualityColors, QualityNames } from '@/types';
import { formatWeight, formatLength } from '@/utils';
import { FishipediaView } from '@/components/fishipedia';

const qualityOrder: Quality[] = [
  Quality.COMMON,
  Quality.UNCOMMON,
  Quality.RARE,
  Quality.EPIC,
  Quality.LEGENDARY,
  Quality.UNIQUE,
  Quality.QUEST,
];

/** CSS filter to hue-shift the default yellow lock emoji to each quality color */
const QualityEmojiFilter: Record<Quality, string> = {
  [Quality.COMMON]: 'grayscale(1) brightness(2)',                              // White
  [Quality.UNCOMMON]: 'hue-rotate(75deg) saturate(2) brightness(1.1)',         // Green
  [Quality.RARE]: 'hue-rotate(180deg) saturate(3) brightness(1.1)',            // Blue
  [Quality.EPIC]: 'hue-rotate(230deg) saturate(2) brightness(1.1)',            // Purple
  [Quality.LEGENDARY]: 'hue-rotate(-10deg) saturate(2) brightness(1.1)',       // Orange
  [Quality.UNIQUE]: 'saturate(2) brightness(1.2)',                             // Gold (enhance native)
  [Quality.QUEST]: 'hue-rotate(160deg) saturate(2) brightness(1.3)',           // Light Blue
};

export function CompactDiscoveryPanel() {
  const [expandedFishId, setExpandedFishId] = useState<string | null>(null);
  const [fishipediaOpen, setFishipediaOpen] = useState(false);

  const currentZone = useGameStore((s) => s.playerState.currentZone);
  const discoveredFish = useGameStore((s) => s.playerState.discoveredFish);
  const protectedFish = useGameStore((s) => s.playerState.autoSellSettings.protectedFish);
  const toggleFishProtection = useGameStore((s) => s.toggleFishProtection);

  const zone = getZoneDefinition(currentZone);
  const zoneDiscoveries = discoveredFish.filter((d) =>
    zone.fishPool.includes(d.definitionId),
  );

  if (zoneDiscoveries.length === 0) {
    return (
      <div className="p-4 text-center">
        <p className="text-lg mb-1">📖</p>
        <p className="text-sm text-gray-400">No fish discovered yet.</p>
        <p className="text-xs text-gray-500 mb-3">Start fishing to discover species!</p>
        <button
          onClick={() => setFishipediaOpen(true)}
          className="text-xs text-blue-400 hover:text-blue-300 bg-blue-900/20 hover:bg-blue-900/30 border border-blue-700/30 rounded-lg py-2 px-4 transition-colors"
        >
          View Fishipedia
        </button>
        {fishipediaOpen && (
          <FishipediaView onClose={() => setFishipediaOpen(false)} />
        )}
      </div>
    );
  }

  return (
    <div className="p-3 space-y-1">
      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1 mb-2">
        Discovered Species
      </h3>

      {/* Fishipedia button */}
      <div className="px-1 mb-2">
        <button
          onClick={() => setFishipediaOpen(true)}
          className="w-full text-xs text-blue-400 hover:text-blue-300 bg-blue-900/20 hover:bg-blue-900/30 border border-blue-700/30 rounded-lg py-2 transition-colors"
        >
          View Fishipedia
        </button>
      </div>

      {fishipediaOpen && (
        <FishipediaView onClose={() => setFishipediaOpen(false)} />
      )}

      {zoneDiscoveries.map((discovery) => {
        const def = getFishDefinition(discovery.definitionId);
        const isExpanded = expandedFishId === discovery.definitionId;
        const discoveredQualities = qualityOrder.filter((q) =>
          discovery.qualitiesSeen.includes(q),
        );

        return (
          <div key={discovery.definitionId}>
            {/* Compact fish row */}
            <button
              onClick={() => setExpandedFishId(isExpanded ? null : discovery.definitionId)}
              className={`w-full text-left rounded-lg p-2.5 border transition-colors ${
                isExpanded
                  ? 'bg-gray-700/60 border-gray-600'
                  : 'bg-gray-800/40 border-transparent hover:bg-gray-700/40 hover:border-gray-700'
              }`}
            >
              {/* Row 1: Name + Records */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-lg shrink-0">{def.emoji}</span>
                  <span className="text-sm font-medium text-white truncate">{def.name}</span>
                </div>
                <span className="text-gray-500 text-[10px] shrink-0">{isExpanded ? '▾' : '▸'}</span>
              </div>

              {/* Row 2: Records + Lock indicators */}
              <div className="flex items-center justify-between mt-1.5 pl-7">
                {/* Records */}
                <div className="flex gap-3 text-[10px] text-gray-400">
                  {discovery.records && discovery.records.maxLength > 0 && (
                    <span>📏 {formatLength(discovery.records.maxLength)}</span>
                  )}
                  {discovery.records && discovery.records.maxWeight > 0 && (
                    <span>⚖️ {formatWeight(discovery.records.maxWeight)}</span>
                  )}
                </div>

                {/* Lock indicators */}
                <div className="flex gap-1">
                  {discoveredQualities.map((quality) => {
                    const isProtected = protectedFish[discovery.definitionId]?.[quality] === true;
                    return (
                      <span
                        key={quality}
                        className="text-xs"
                        style={{
                          filter: isProtected ? QualityEmojiFilter[quality] : 'grayscale(1) brightness(0.6)',
                        }}
                        title={`${QualityNames[quality]}: ${isProtected ? 'Protected' : 'Unprotected'}`}
                      >
                        {isProtected ? '🔒' : '🔓'}
                      </span>
                    );
                  })}
                </div>
              </div>
            </button>

            {/* Expanded: Quality lock grid */}
            {isExpanded && (
              <div className="mt-1 ml-7 mr-1 mb-2 grid grid-cols-2 gap-1.5">
                {discoveredQualities.map((quality) => {
                  const isProtected = protectedFish[discovery.definitionId]?.[quality] === true;
                  return (
                    <button
                      key={quality}
                      onClick={() => toggleFishProtection(discovery.definitionId, quality)}
                      className={`rounded p-2 border transition-all hover:scale-105 active:scale-95 text-center ${
                        isProtected
                          ? 'bg-blue-600/20 border-blue-500/50 hover:bg-blue-600/30'
                          : 'bg-gray-800/50 border-gray-600/50 hover:border-gray-500'
                      }`}
                    >
                      <div
                        className="text-xs font-semibold"
                        style={{ color: QualityColors[quality] }}
                      >
                        {QualityNames[quality]}
                      </div>
                      <div
                        className="text-sm mt-0.5"
                        style={{
                          filter: isProtected ? QualityEmojiFilter[quality] : 'grayscale(1) brightness(0.6)',
                        }}
                      >
                        {isProtected ? '🔒' : '🔓'}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
